import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { MutableRefObject, useCallback, useMemo, useState } from 'react';

import {
  SavedReportSettings,
  SavedReportSettingsType,
  UpdatableSavedReportSettingsData,
  useSavedReportSettings,
} from '~/data/reportSettings';
import { UseReportDatasetOutput } from '~/scenes/PortalModules/Analytics/common';
import {
  getResetColumnVisibilityModel,
  resetDataGridSettings,
  validateSavedReportSettings,
} from '~/scenes/PortalModules/Analytics/common/SavedReportSettingsSelector/data';

type PropsType<P> = {
  reportType: SavedReportSettingsType;
  reportDataset?: UseReportDatasetOutput<P>;
  apiRef: MutableRefObject<GridApiPremium | null>;
};

export type UseSavedReportSettingsConfigOutput<P> = {
  reportSettingOptions: SavedReportSettings<P>[];
  selectedReportSettings?: SavedReportSettings<P>;
  onChangeSelectedReport: (reportSettingsId?: string) => void;
  onDeleteSelectedReportSettings: () => void;
  onCreateReportSettings: (
    createSettings: UpdatableSavedReportSettingsData,
  ) => Promise<SavedReportSettings<P>>;
  onUpdateSelectedReportSettings: (
    updatedSettings: Partial<UpdatableSavedReportSettingsData>,
  ) => Promise<SavedReportSettings<P>>;
};

export const useSavedReportSettingsConfig = <P>(
  props: PropsType<P>,
): UseSavedReportSettingsConfigOutput<P> => {
  const { reportType, reportDataset, apiRef } = props;

  const {
    data: unfilteredData,
    onDeleteReportSettings,
    onSaveReportSettings,
  } = useSavedReportSettings<P>(props);

  const data = useMemo(() => {
    return validateSavedReportSettings(unfilteredData, reportDataset);
  }, [unfilteredData, reportDataset]);

  const [selectedReportSettingsId, setSelectedReportSettingsId] = useState<
    string | undefined
  >(undefined);

  const selectedReportSettings = useMemo(() => {
    if (!!selectedReportSettingsId) {
      return data.find(({ id }) => id === selectedReportSettingsId);
    }

    return undefined;
  }, [selectedReportSettingsId, data]);

  const onChangeSelectedReport: UseSavedReportSettingsConfigOutput<P>['onChangeSelectedReport'] =
    useCallback(
      (reportSettingsId?: string) => {
        const foundSettings = data.find(({ id }) => id === reportSettingsId);

        if (!!reportDataset) {
          const { setReportSelection, defaultReportSelection } = reportDataset;

          setSelectedReportSettingsId(
            !!foundSettings ? reportSettingsId : undefined,
          );
          if (!!foundSettings && !!foundSettings.settingsConfig) {
            const {
              reportSelection,
              columnVisibility,
              columnSorting,
              columnFiltering,
            } = foundSettings.settingsConfig;

            setReportSelection(
              !!reportSelection ? reportSelection : defaultReportSelection,
            );

            if (!!apiRef.current) {
              apiRef.current.setColumnVisibilityModel(
                !!columnVisibility
                  ? columnVisibility
                  : getResetColumnVisibilityModel(
                      apiRef.current.getAllColumns(),
                    ),
              );
              apiRef.current.setSortModel(!!columnSorting ? columnSorting : []);
              apiRef.current.setFilterModel(
                columnFiltering ? columnFiltering : { items: [] },
                'restoreState',
              );
            }
          } else {
            setReportSelection(defaultReportSelection);
            resetDataGridSettings(apiRef);
          }
        }
      },
      [data, setSelectedReportSettingsId, reportDataset, apiRef],
    );

  const onCreateReportSettings: UseSavedReportSettingsConfigOutput<P>['onCreateReportSettings'] =
    useCallback(
      async (createSettings: UpdatableSavedReportSettingsData) => {
        return onSaveReportSettings({
          data: { ...createSettings, reportType },
        }).then((data) => {
          if (!!data.id) {
            setSelectedReportSettingsId(data.id);
          }

          return data;
        });
      },
      [onSaveReportSettings, reportType, setSelectedReportSettingsId],
    );

  const onUpdateSelectedReportSettings: UseSavedReportSettingsConfigOutput<P>['onUpdateSelectedReportSettings'] =
    useCallback(
      (updatedSettings: Partial<UpdatableSavedReportSettingsData>) => {
        if (!!selectedReportSettings) {
          return onSaveReportSettings({
            reportSettingsId: selectedReportSettings.id,
            data: { ...selectedReportSettings, ...updatedSettings },
          });
        }

        return Promise.reject(
          'onUpdateSelectedReportSettings: No selected report settings found',
        );
      },
      [selectedReportSettings, onSaveReportSettings],
    );

  const onDeleteSelectedReportSettings: UseSavedReportSettingsConfigOutput<P>['onDeleteSelectedReportSettings'] =
    useCallback(() => {
      if (!!selectedReportSettings) {
        return onDeleteReportSettings(selectedReportSettings.id);
      }
    }, [onDeleteReportSettings, selectedReportSettings]);

  return {
    reportSettingOptions: data,
    onChangeSelectedReport,
    selectedReportSettings,
    onDeleteSelectedReportSettings,
    onCreateReportSettings,
    onUpdateSelectedReportSettings,
  };
};
